import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { asset } from "@/lib/assets";
const hvOldExperience = asset("case-studies/quiz/hv_old-experience.png");
const hvUIExplorations = asset("case-studies/quiz/hv-UI-explorations.png");
const variantsInTheWild = asset("case-studies/quiz/variantsinthewild.png");
const sellingQuestions = asset("case-studies/quiz/selling-questions.png");
const piiScreen = asset("case-studies/quiz/PII.png");
const hvTestResults1 = asset("case-studies/quiz/HV-test-results.png");
const hvTestResults2 = asset("case-studies/quiz/HV-test-results2.png");
const finalDesign = asset("case-studies/quiz/finaldesign.png");
const newUI = asset("case-studies/quiz/newui.png");
const quizOpt1 = asset("case-studies/quiz/quiz-opt1.mp4");
const quizOpt2 = asset("case-studies/quiz/quiz-opt2.mp4");

const META = [
  { label: "Role", value: "Product Designer" },
  { label: "Scope", value: "UX · UI · Usability Testing · AI Prototyping" },
  { label: "Tools", value: "Figma · Figma Make · Maze · Cursor · Hotjar" },
  { label: "Time", value: "4 weeks" },
];

const APPROACH_STEPS = [
  { tool: "Figma + Figma Make", label: "Design exploration and definition" },
  { tool: "Cursor + Figma MCP", label: "Launch variant" },
  { tool: "Periscope", label: "Review quantitative data" },
  { tool: "Maze", label: "User testing for qualitative data" },
  { tool: "", label: "Iterate and repeat" },
];

const USABILITY_INSIGHTS = [
  {
    insight:
      "Users who might be interested in selling their home felt pressured by repetitive questions about selling. These questions were originally added for lead qualification but negatively affected conversion.",
    imageAlt: "Selling questions screen",
    imageSrc: sellingQuestions,
  },
  {
    insight:
      "50% of participants mentioned the PII contact form step as the most confusing or uncertain part of the flow. We did not say upfront that contact information would be required, and users didn't understand who would receive their information.",
    quote: "\"I'll Google the company first.\"",
    quoteNote: "This insight directly triggered one of the improvements.",
    imageAlt: "PII contact form screen",
    imageSrc: piiScreen,
  },
];

const NAV_SECTIONS = [
  { id: "tl-dr", label: "TL;DR" },
  { id: "overview", label: "Overview" },
  { id: "approach", label: "Approach" },
  { id: "design-exploration", label: "Design Exploration" },
  { id: "ai-build", label: "AI Build" },
  { id: "usability-testing", label: "Usability Testing" },
  { id: "design-improvements", label: "Improvements" },
  { id: "impact", label: "Impact" },
  { id: "key-takeaway", label: "Key Takeaway" },
];

function ImageSlot({
  src,
  alt,
  className = "",
  objectFit = "cover",
  caption,
}: {
  src: string;
  alt: string;
  className?: string;
  objectFit?: "cover" | "contain";
  caption?: string;
}) {
  const [lightbox, setLightbox] = useState(false);
  return (
    <>
      <div className="w-full">
      <div
        className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl overflow-hidden flex items-center justify-center cursor-zoom-in ${className}`}
        onClick={() => setLightbox(true)}
        title="Click to expand"
      >
        <img
          src={src}
          alt={alt}
          className={`w-full h-full ${objectFit === "contain" ? "object-contain" : "object-cover"}`}
          onError={(e) => {
            const el = e.currentTarget as HTMLImageElement;
            el.style.display = "none";
            const parent = el.parentElement;
            if (parent && !parent.querySelector(".placeholder-label")) {
              const label = document.createElement("p");
              label.className =
                "placeholder-label text-xs text-[#888888] text-center p-4";
              label.textContent = alt;
              parent.appendChild(label);
            }
          }}
        />
      </div>
      {caption && <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">{caption}</p>}
      </div>
      {lightbox && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(false)}
        >
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
            aria-label="Close"
          >×</button>
        </div>,
        document.body
      )}
    </>
  );
}

function VideoSlot({ src, className = "" }: { src: string; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { video.play().catch(() => {}); }
        else { video.pause(); }
      },
      { threshold: 0.3 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);
  return (
    <div className={`rounded-2xl overflow-hidden bg-[#EEF2FF] border border-[#E2E8F0] ${className}`}>
      <video ref={videoRef} src={src} loop muted playsInline controls className="w-full h-full object-cover" />
    </div>
  );
}

function InsightCallout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3 bg-[#FDF3E7] border border-[#F5C542]/20 rounded-2xl p-5">
      <span className="text-xl shrink-0 mt-0.5">💡</span>
      <p className="text-[#7A4A1A] text-[0.9375rem] leading-[1.75]">{children}</p>
    </div>
  );
}

function SidebarNav({ activeSection, visible }: { activeSection: string; visible: boolean }) {
  return (
    <nav className={`hidden xl:block fixed left-8 top-1/2 -translate-y-1/2 z-40 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
      <div className="relative flex flex-col">
        {/* Vertical track */}
        <div className="absolute left-[5px] top-3 bottom-3 w-px bg-[#E2E8F0]" />
        {NAV_SECTIONS.map((s) => {
          const isActive = activeSection === s.id;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" });
              }}
              className="flex items-center gap-3 py-[7px] group"
            >
              <div
                className={`w-[11px] h-[11px] rounded-full border-2 shrink-0 relative z-10 transition-all duration-200 ${
                  isActive
                    ? "border-[#F5C542] bg-[#F5C542] scale-110"
                    : "border-[#D4CFC9] bg-[#F7F8FA] group-hover:border-[#F5C542]/60"
                }`}
              />
              <span
                className={`text-xs transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? "text-[#1A1A1A] font-semibold"
                    : "text-[#AAAAAA] group-hover:text-[#555]"
                }`}
              >
                {s.label}
              </span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}

export function CaseStudyQuiz() {
  const [activeSection, setActiveSection] = useState("tl-dr");
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const hero = heroRef.current;
      if (!hero) return;
      setSidebarVisible(hero.getBoundingClientRect().bottom <= 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-15% 0px -75% 0px", threshold: 0 }
    );

    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F8FA] font-[DM_Sans]">
      <SidebarNav activeSection={activeSection} visible={sidebarVisible} />

      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#F7F8FA]/90 backdrop-blur-sm border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
          >
            ← All work
          </Link>
          <Link
            to="/"
            className="font-[Bricolage_Grotesque] font-bold text-sm text-[#1A1A1A]"
          >
            Verónica <span className="text-[#1D3FE8]">Aguilar</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div ref={heroRef} className="max-w-5xl mx-auto px-6 sm:px-12 pt-24 pb-20">
        <AnimatedSection>
          <h1 className="font-[Bricolage_Grotesque] font-extrabold tracking-[-0.04em] text-[clamp(2.25rem,5vw,3.75rem)] text-[#1A1A1A] leading-[1.05] mb-6">
            Home Value{" "}
            <span className="text-[#1D3FE8]">Quiz Optimization.</span>
          </h1>
          <p className="text-[#555] text-lg leading-[1.8] max-w-[680px] mb-12">
            Improving a high-traffic lead generation experience used by ~1,700
            daily users through rapid experimentation in production and
            AI-assisted prototyping.
          </p>

          {/* Meta strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-[#E2E8F0]">
            {META.map((m) => (
              <div key={m.label}>
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                  {m.label}
                </p>
                <p className="text-[0.9375rem] font-medium text-[#1A1A1A] leading-snug">
                  {m.value}
                </p>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>

      {/* Metrics banner */}
      <AnimatedSection>
        <div className="bg-[#1A1A1A] px-12 py-12 grid sm:grid-cols-2 gap-0">
          <div className="sm:border-r border-white/[0.08] sm:pr-10 sm:text-center">
            <p className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(2.5rem,5vw,3.5rem)] tracking-[-0.04em] leading-none text-[#F5C542] mb-2">
              16.40%
            </p>
            <p className="text-white/50 text-sm leading-relaxed">
              Quiz-to-lead conversion rate
              <br />
              <span className="text-xs opacity-50">Up from 13.03% baseline</span>
            </p>
          </div>
          <div className="sm:pl-10 mt-8 sm:mt-0 sm:text-center">
            <p className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(2.5rem,5vw,3.5rem)] tracking-[-0.04em] leading-none text-[#F5C542] mb-2">
              8
            </p>
            <p className="text-white/50 text-sm leading-relaxed">
              Variants tested in production
              <br />
              <span className="text-xs opacity-50">
                Idea → live test in days, not sprints
              </span>
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Sections */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12">

        {/* TL;DR */}
        <div id="tl-dr">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              TL;DR
            </p>
            <p className="text-[#444] text-[1.0625rem] leading-[1.85] max-w-[740px]">
              HomeLight is a lead generation channel for real estate agents. The
              home value quiz on our website drives thousands of weekly users.
              Through usability testing, AI-assisted prototyping, and multiple
              tests in production, we improved quiz-to-lead conversion from{" "}
              <strong className="text-[#1A1A1A] font-semibold">
                13.03% → 16.40%
              </strong>{" "}
              while establishing a faster experimentation workflow.
            </p>
          </AnimatedSection>
        </div>

        {/* Overview */}
        <div id="overview">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Overview
            </p>
            <div className="grid sm:grid-cols-[1.2fr_1fr] gap-16 items-start mb-12">
              <div>
                <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-5">
                  A high-traffic quiz that no longer{" "}
                  <span className="text-[#1D3FE8]">matched user expectations.</span>
                </h2>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] mb-4">
                  HomeLight's home value quiz is a major source of inbound leads,
                  receiving ~1,700 users per day. However, the experience had
                  evolved over the years and no longer aligned with user
                  expectations.
                </p>
                <ul className="flex flex-col gap-2">
                  {[
                    "The flow didn't match users' mental models",
                    "Users were asked to create an account before understanding the value of completing the flow.",
                    <strong key="bold" className="font-semibold text-[#1A1A1A]">The home value was never revealed</strong>,
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex gap-3 text-[#444] text-[0.9375rem] leading-[1.7]"
                    >
                      <span className="text-[#1D3FE8] mt-1 shrink-0">—</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-[#888888] text-sm text-center hidden sm:block" />
            </div>
            <ImageSlot
              src={hvOldExperience}
              alt="Current quiz experience — GIF"
              className="w-full aspect-video"
            />
            <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">Baseline experience</p>
          </AnimatedSection>
        </div>

        {/* Approach */}
        <div id="approach">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Approach
            </p>
            <div className="grid sm:grid-cols-[1.2fr_1fr] gap-16 items-start">
              <div>
                <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-5">
                  Rapid experimentation in production.
                </h2>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] mb-8">
                  Instead of committing to one direction, we focused on testing
                  multiple variants simultaneously. Each experiment received ~10%
                  of daily traffic, allowing us to validate ideas quickly.
                </p>
                <div className="flex flex-col gap-0">
                  {APPROACH_STEPS.map((step, i) => (
                    <div
                      key={i}
                      className="flex gap-4 items-start py-4 border-b border-[#E2E8F0] last:border-b-0"
                    >
                      <span className="font-[Bricolage_Grotesque] font-bold text-[#1D3FE8] text-sm w-6 shrink-0 mt-0.5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <p className="text-[#1A1A1A] font-medium text-[0.9375rem]">
                          {step.label}
                        </p>
                        {step.tool && (
                          <p className="text-xs text-[#888888] mt-0.5">
                            {step.tool}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div />
            </div>
          </AnimatedSection>
        </div>

        {/* Design Exploration */}
        <div id="design-exploration">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Design Exploration
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-5">
              Three directions. Built fast with AI.
            </h2>
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10">
              Each stakeholder and team member had different ideas on how to solve
              this, so I explored different variants and ran design critiques with
              the team. Three different flows were prototyped, each testing
              variations in question structure, pacing and transitions, and when
              the home value was revealed. To accelerate exploration, I used Figma
              Make to quickly generate interactive prototypes with animations and
              transitions.
            </p>

            <InsightCallout>
              Stakeholders and team members are more engaged when I show
              functional prototypes rather than static screens.
            </InsightCallout>

            <div className="grid sm:grid-cols-2 gap-4 mt-6 mb-6">
              <VideoSlot src={quizOpt1} className="aspect-video" />
              <VideoSlot src={quizOpt2} className="aspect-video" />
            </div>
            <ImageSlot
              src={hvUIExplorations}
              alt="Design explorations"
              className="w-full aspect-square"
              objectFit="contain"
            />
            <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">Design explorations</p>
          </AnimatedSection>
        </div>

        {/* AI-Assisted Build Workflow */}
        <div id="ai-build">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              AI-Assisted Build Workflow
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-5">
              Design to production. In parallel.
            </h2>
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-4">
              While I worked on the design direction, the engineer and AI builder
              set up a new repo, added our design system using our new components
              library (
              <Link
                to="/case-study/design-system"
                className="text-[#1D3FE8] underline underline-offset-2 hover:opacity-70 transition-opacity"
              >
                see design system case study
              </Link>
              ), and planned how the variants would split our traffic.
            </p>
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-4">
              Initial prompts from my peers produced ~65% of the front-end
              implementation.
            </p>
            <div className="flex gap-3 items-baseline max-w-[680px] mb-4">
              <span className="text-[#1D3FE8] font-bold text-base shrink-0">→</span>
              <p className="text-[0.9375rem] leading-[1.85] text-[#1A1A1A]">
                <strong className="font-semibold">I ran quick QA reviews and fixed UI details directly in the code that did not match Figma specs, as well as issues I only noticed once the design was live — like colors that looked too interactive in elements that were not.</strong>
              </p>
            </div>
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-8">
              This workflow made it very easy to create additional variants and
              iterate on the design.
            </p>
            <InsightCallout>
              Teamwork was key in this project. We stayed in sync and made
              progress in parallel, which allowed us to move from design to
              production significantly faster.
            </InsightCallout>
            <ImageSlot
              src={variantsInTheWild}
              alt="Variants in the wild"
              className="w-full aspect-video mt-8"
              objectFit="contain"
              caption="Visualization of all variants. We documented them on Figma for a visual reference of all the changes"
            />
          </AnimatedSection>
        </div>

        {/* Usability Testing */}
        <div id="usability-testing">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Usability Testing
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-5">
              8 variants in. Still stuck. Time to ask users.
            </h2>
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-5">
              This project moved fast. We reached 8 variants in just a couple of
              weeks. At some point we were stuck and didn't understand what could
              push the conversion higher. Since we already had quantitative data
              in Periscope from all the variants running in production, the next
              step was to collect qualitative insights.
            </p>
            <div className="flex gap-3 items-baseline max-w-[680px] mb-6">
              <span className="text-[#1D3FE8] font-bold text-base shrink-0">→</span>
              <p className="text-[0.9375rem] leading-[1.85] text-[#1A1A1A]">
                <strong className="font-semibold">I took the best performing variant and created a few tests in Maze to gather user feedback.</strong>
              </p>
            </div>
            <div className="mb-10">
              <InsightCallout>
                Working with a free Maze account is restrictive because you cannot
                ask more than 6 questions, so I focused on the main drop-off
                points in the flow: "Adding home details" and the "PII / contact
                information" step.
              </InsightCallout>
            </div>

            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-6">
              Key insights
            </p>
            <div className="flex flex-col gap-6 mb-12">
              {USABILITY_INSIGHTS.map((item, i) => (
                <div
                  key={i}
                  className="grid sm:grid-cols-[1fr_1fr] gap-6 bg-white border border-[#E2E8F0] rounded-2xl p-6"
                >
                  <div className="flex flex-col justify-center">
                    <p className="text-[#1A1A1A] text-[0.9375rem] leading-[1.8] mb-3">
                      {item.insight}
                    </p>
                    {item.quote && (
                      <div className="border-l-2 border-[#F5C542] pl-4 mt-2">
                        <p className="text-[#1D3FE8] font-medium text-sm italic">
                          {item.quote}
                        </p>
                        {item.quoteNote && (
                          <p className="text-[#888888] text-xs mt-1">
                            {item.quoteNote}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <ImageSlot
                    src={item.imageSrc}
                    alt={item.imageAlt}
                    className="aspect-[4/3]"
                  />
                </div>
              ))}
            </div>

            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-4">
              Test results
            </p>
            <div className="flex flex-col gap-4">
              <ImageSlot
                src={hvTestResults1}
                alt="Maze test results — screen 1"
                className="w-full aspect-[16/9]"
              />
              <ImageSlot
                src={hvTestResults2}
                alt="Maze test results — screen 2"
                className="w-full aspect-[16/9]"
              />
            </div>
          </AnimatedSection>
        </div>

        {/* Design Improvements */}
        <div id="design-improvements">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Design Improvements
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-12">
              Based on the usability insights, we introduced three changes.
            </h2>

            {/* Improvement 1 */}
            <div className="mb-16">
              <div className="flex gap-4 items-baseline mb-5">
                <span className="font-[Bricolage_Grotesque] font-bold text-[#1D3FE8] text-sm shrink-0">
                  01
                </span>
                <h3 className="font-[Bricolage_Grotesque] font-bold text-xl text-[#1A1A1A]">
                  Increase transparency
                </h3>
              </div>
              <ul className="flex flex-col gap-2 mb-6 ml-10">
                {[
                  "Mention upfront that the final home value will be shown at the end",
                  "Include disclaimers explaining how information is used",
                  "Add reassurance messaging clarifying users are not committing to sell",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-[#444] text-[0.9375rem] leading-[1.7]"
                  >
                    <span className="text-[#1D3FE8] shrink-0">—</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Improvement 2 */}
            <div className="mb-16">
              <div className="flex gap-4 items-baseline mb-5">
                <span className="font-[Bricolage_Grotesque] font-bold text-[#1D3FE8] text-sm shrink-0">
                  02
                </span>
                <h3 className="font-[Bricolage_Grotesque] font-bold text-xl text-[#1A1A1A]">
                  Build trust in the contact step
                </h3>
              </div>
              <ul className="flex flex-col gap-2 ml-10">
                {[
                  "Add social proof to the contact form screen",
                  "Clarify next steps and expectations after submission",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-[#444] text-[0.9375rem] leading-[1.7]"
                  >
                    <span className="text-[#1D3FE8] shrink-0">—</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Improvement 3 */}
            <div className="mb-12">
              <div className="flex gap-4 items-baseline mb-5">
                <span className="font-[Bricolage_Grotesque] font-bold text-[#1D3FE8] text-sm shrink-0">
                  03
                </span>
                <h3 className="font-[Bricolage_Grotesque] font-bold text-xl text-[#1A1A1A]">
                  Improve selling question logic
                </h3>
              </div>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] ml-10">
                We made the selling questions dynamic. If a user indicates they are
                not interested in selling, we skip follow-up questions related to
                selling.
              </p>
            </div>

            {/* Winning design */}
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-4">
              Winning design
            </p>
            <ImageSlot
              src={finalDesign}
              alt="Final quiz experience — GIF"
              className="w-full aspect-[4/3] mb-6"
              objectFit="contain"
            />
            <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px]">
              Before these changes our conversion was slightly above the baseline
              (~14%). After implementing them we reached 16.40% (as of March 16,
              2026). After graduating from this experiment, this version is
              becoming the new baseline for the next round of improvements.
            </p>
          </AnimatedSection>
        </div>

        {/* Impact */}
        <div id="impact">
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Impact
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-10">
              Numbers that moved. And a process that changed.
            </h2>

            {/* Primary metrics */}
            <div className="bg-[#1A1A1A] rounded-2xl p-8 mb-8 relative overflow-hidden">
              <div className="absolute top-[-60px] right-[-60px] w-48 h-48 bg-[#F5C542] rounded-full opacity-10" />
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#F5C542] mb-6 relative z-10">
                Conversion
              </p>
              <div className="grid sm:grid-cols-3 gap-8 relative z-10">
                <div>
                  <p className="font-[Bricolage_Grotesque] font-bold text-white text-lg mb-1">
                    13.03% → 16.40%
                  </p>
                  <p className="text-white/50 text-sm leading-relaxed mb-3">
                    Quiz-to-lead conversion improvement
                  </p>
                  <p className="text-xs font-medium uppercase tracking-[0.07em] text-white/30 mb-2">
                    Secondary metrics monitored
                  </p>
                  <ul className="flex flex-col gap-1">
                    {[
                      "Lead → meeting scheduled in 1 day",
                      "Lead → meeting scheduled in 7 days",
                      "RPL (Revenue per Lead)",
                    ].map((item) => (
                      <li key={item} className="flex gap-2 text-white/40 text-xs leading-[1.6]">
                        <span className="shrink-0">—</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-[Bricolage_Grotesque] font-bold text-white text-lg mb-1">
                    ↓ Drop-off
                  </p>
                  <p className="text-white/50 text-sm leading-relaxed">
                    Reduced drop-off in the contact information step
                  </p>
                </div>
                <div>
                  <p className="font-[Bricolage_Grotesque] font-bold text-white text-lg mb-1">
                    Social proof
                  </p>
                  <p className="text-white/50 text-sm leading-relaxed">
                    Improved trust in the experience, now replicated across other quiz flows
                  </p>
                </div>
              </div>
            </div>

            {/* Process impact dark card */}
            <div className="bg-[#1A1A1A] rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute bottom-[-60px] left-[-60px] w-48 h-48 bg-[#F5C542] rounded-full opacity-10" />
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#F5C542] mb-6 relative z-10">
                Process impact
              </p>
              <div className="grid sm:grid-cols-3 gap-8 relative z-10">
                {[
                  "Established a faster experimentation model",
                  "Introduced AI-assisted prototyping and implementation",
                  "Reduced time from idea → production test",
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="text-[#F5C542] font-bold shrink-0 mt-0.5">—</span>
                    <p className="text-white/70 text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Next Steps */}
        <div>
          <AnimatedSection className="py-20 border-b border-[#E2E8F0]">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-4">
              Next steps
            </p>
            <div className="flex gap-3 items-baseline max-w-[680px] mb-8">
              <span className="text-[#1D3FE8] font-bold text-base shrink-0">→</span>
              <p className="text-[0.9375rem] leading-[1.85] text-[#1A1A1A]">
                <strong className="font-semibold">Next idea that came from a second usability test is to add images and improve the UI to make it more intuitive for users to answer.</strong>
              </p>
            </div>
            <ImageSlot
              src={newUI}
              alt="New UI with home condition question images"
              className="w-full aspect-[16/7]"
              objectFit="contain"
            />
          </AnimatedSection>
        </div>

        {/* Key Takeaway */}
        <div id="key-takeaway">
          <AnimatedSection className="py-20">
            <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
              <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
              Key Takeaway
            </p>
            <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-10">
              Velocity is a superpower. Testing is non-negotiable.
            </h2>

            <div className="flex flex-col gap-6">
              {[
                {
                  label: "Speed without research has a ceiling",
                  body: "With all the new technology it's easy to overlook some steps in the design process. However, this project was a big lesson that even with the velocity on our side, things like testing the ideas with real users was pivotal for the project. Getting a budget for tools like Maze was difficult with leadership, but this project is now setting the standard.",
                },
                {
                  label: "Design is not a linear process",
                  body: "I confirmed that project development is not a linear process, especially in this era. It's a mix and match of steps that should adapt to the project scope and needs. As a team we have made it a conscious practice to include user research — even lightweight studies — as a core part of our experimentation loop.",
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <span className="mt-1 text-[#1D3FE8] font-[Bricolage_Grotesque] font-bold text-sm w-6 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] mb-2">
                      {item.label}
                    </p>
                    <p className="text-[#555] text-[0.9375rem] leading-[1.85]">
                      {item.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>

        {/* Footer nav */}
        <div className="border-t border-[#E2E8F0] py-16 flex items-center justify-between">
          <Link
            to="/#work"
            className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
          >
            ← All case studies
          </Link>
          <div className="text-right">
            <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
              Next case study
            </p>
            <Link
              to="/case-study/homelight-offers"
              className="font-[Bricolage_Grotesque] font-extrabold text-lg tracking-[-0.02em] text-[#1A1A1A] hover:text-[#1D3FE8] transition-colors"
            >
              Shipping Products. →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
